import { useEffect, useState } from "react";
import { omit } from "lodash";

import { IBuilding } from "../../interfaces/interfaces";

interface ITimeZone {
  identifier: string;
  value: string;
}

const renderSelectStates = (option: string) => (
  <option className="scheduler-select-option menu" key={option} value={option}>
    {option}
  </option>
);

const renderSelectTimezones = (timeZone: ITimeZone) => (
  <option
    className="scheduler-select-option menu"
    key={timeZone.identifier}
    value={timeZone.identifier}
  >
    {timeZone.value}
  </option>
);

const states = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

const timeZoneMap: ITimeZone[] = [
  {
    identifier: "America/New_York",
    value: "Eastern",
  },
  {
    identifier: "America/Chicago",
    value: "Central",
  },
  {
    identifier: "America/Denver",
    value: "Mountain",
  },
  {
    identifier: "America/Los_Angeles",
    value: "Pacific",
  },
  {
    identifier: "America/Anchorage",
    value: "Alaska",
  },
  {
    identifier: "Pacific/Honolulu",
    value: "Hawaii-Aleutian",
  },
  {
    identifier: "America/Halifax",
    value: "Atlantic",
  },
  {
    identifier: "America/Phoenix",
    value: "Arizona (no DST)",
  },
];

const BuildingForm: React.FC<{
  closeModal: () => void;
  handleSubmit(e: React.FormEvent<HTMLFormElement>): void;
  building?: IBuilding;
}> = ({ closeModal, handleSubmit, building }) => {
  const [formData, setFormData] = useState(
    building
      ? {
          confirmPropertyHomePasscode: "",
          propertyAddressLine1: building.addressLine1,
          propertyAddressLine2: building.addressLine2,
          propertyCity: building.city,
          propertyHomePasscode:
            building.buildingWideSettings.defaultHomePasscode,
          propertyName: building.propertyName,
          propertyState: building.state,
          propertyTimeZone: building.buildingWideSettings.localTimezone,
          propertyZipCode: building.zipcode,
        }
      : {
          confirmPropertyHomePasscode: "",
          propertyAddressLine1: "",
          propertyAddressLine2: "",
          propertyCity: "",
          propertyHomePasscode: "",
          propertyName: "",
          propertyState: "",
          propertyTimeZone: "",
          propertyZipCode: "",
        }
  );
  const [isFormEmpty, setIsFormEmpty] = useState(true);

  useEffect(() => {
    const filteredFormData = omit(formData, "propertyAddressLine2");
    setIsFormEmpty(
      !Object.values(filteredFormData).some((value) => value === "") &&
        formData.propertyHomePasscode === formData.confirmPropertyHomePasscode
    );
  }, [formData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-create-body-inner">
        <div className="modal-input-container">
          <label htmlFor="propertyName" data-testid="create-organization-name">
            Property Name
          </label>
          <input
            className="modal-input modal-input-small"
            name="propertyName"
            value={formData.propertyName}
            onChange={handleChange}
          />
        </div>
        <div className="modal-input-container">
          <label htmlFor="propertyTimeZone">Time Zone</label>
          <select
            data-testid="smart-card-credential-select"
            className="tenants-show-credentials-select tenants-show-credentials-select-default menu tool-tip "
            name="propertyTimeZone"
            onChange={handleChange}
            value={formData.propertyTimeZone}
          >
            <option className="scheduler-select-option menu" key="Select">
              Select One
            </option>
            {timeZoneMap.map(renderSelectTimezones)}
          </select>
        </div>
        <div />
        <div className="modal-input-container">
          <label htmlFor="propertyAddressLine1">Address Line 1</label>
          <input
            className="modal-input modal-input-small"
            name="propertyAddressLine1"
            value={formData.propertyAddressLine1}
            onChange={handleChange}
          />
        </div>
        <div className="modal-input-container">
          <label htmlFor="propertyAddressLine2">Address Line 2</label>
          <input
            className="modal-input modal-input-small"
            name="propertyAddressLine2"
            value={formData.propertyAddressLine2}
            onChange={handleChange}
          />
        </div>
        <div className="modal-input-container">
          <label htmlFor="propertyCity">City</label>
          <input
            className="modal-input modal-input-small"
            name="propertyCity"
            value={formData.propertyCity}
            onChange={handleChange}
          />
        </div>
        <div className="modal-input-container">
          <label htmlFor="propertyState">State</label>
          <select
            data-testid="smart-card-credential-select"
            className="tenants-show-credentials-select tenants-show-credentials-select-default menu tool-tip "
            name="propertyState"
            onChange={handleChange}
            value={formData.propertyState}
          >
            <option
              className="scheduler-select-option menu"
              key="Select"
              value=""
            >
              Select One
            </option>
            {states.map(renderSelectStates)}
          </select>
        </div>
        <div className="modal-input-container">
          <label htmlFor="propertyZipCode">Zip Code</label>
          <input
            className="modal-input modal-input-small"
            name="propertyZipCode"
            value={formData.propertyZipCode}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="margin-bottom-small" />
      <div className="modal-create-body-inner">
        <div className="modal-input-container">
          <label htmlFor="propertyHomePasscode">Default Home Passcode</label>
          <input
            className="modal-input modal-input-small"
            name="propertyHomePasscode"
            value={formData.propertyHomePasscode}
            onChange={handleChange}
          />
        </div>
        <div className="modal-input-container">
          <label htmlFor="confirmPropertyHomePasscode">
            Confirm Home Passcode
          </label>
          <input
            className="modal-input modal-input-small"
            name="confirmPropertyHomePasscode"
            value={formData.confirmPropertyHomePasscode}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="margin-bottom-small" />
      <div className="modal-input-actions">
        <button
          type="button"
          className="tertiary-btn-small"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!isFormEmpty}
          className="tertiary-btn-small user-card-action-btn user-card-action-btn-resend"
        >
          Confirm
        </button>
      </div>
    </form>
  );
};

export default BuildingForm;
